import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { Node } from 'reactflow';
import {
  Bot,
  Cpu,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Link as LinkIcon,
  Columns2,
  MessagesSquare,
  Box,
  Network,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/OS/components/ui/dialog';
import AgentPingPong3D from '@/components/blueprints/AgentPingPong3D';
import Orchestration3D, { buildOrchestrationFromTheaterHistory } from '@/components/blueprints/Orchestration3D';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SingularityErrorBoundary from '@/components/blueprints/SingularityErrorBoundary';
import ThinkingDots from '@/components/blueprints/ThinkingDots';

// Persist playback state per-run so focus changes / remounts don't restart the timeline.
const __agentTheaterPlaybackStore = new Map<
  string,
  {
    cursor: number;
    playing: boolean;
    finished: boolean;
    lastLen: number;
  }
>();

// Persist Singularity open/closed state per run key so transitions (executing → paused/completed)
// don't unexpectedly close the 3D dialog.
const __singularityOpenStore = new Map<string, boolean>();

export type SourceItem = {
  source: 'local' | 'kb';
  title: string;
  path?: string;
  snippet?: string;
  similarity?: number;
};

export type TheaterHistoryEntry = {
  nodeId: string;
  message: string;
  timestamp: string;
  role?: 'agent' | 'tool' | 'system';
  lane?: string; // 'left' | agentId
  sources?: SourceItem[];
};

type ViewMode = 'pingpong' | 'lanes';

type Props = {
  nodes: Node[];
  history: TheaterHistoryEntry[];
  onActiveNodeIdChange?: (nodeId: string | null) => void;
  /**
   * live: animate new events as they stream in (execution view)
   * saved: show the completed run immediately; user can scrub steps manually (Runs dialog)
   */
  mode?: 'live' | 'saved';
  /**
   * When true, show the BlossomScout (blue orb) in the 3D orchestration view.
   * NOTE: this only affects the 3D Cortex view; it does not start/stop blueprint execution.
   */
  scoutEnabled?: boolean;
  /** If true, show a Scout toggle in the Cortex header. */
  showScoutToggle?: boolean;
  onScoutEnabledChange?: (enabled: boolean) => void;
  /** Keywords the scout should prioritize when choosing targets. */
  scoutKeywords?: string[];
  onScoutKeywordsChange?: (keywords: string[]) => void;
  /** If true, prevent opening the 3D Cortex during execution. */
  cortexDisabled?: boolean;
  /**
   * Override how the 3D Cortex behaves.
   * Useful when the chat UI is live, but the 3D view should behave like a saved run.
   */
  cortexMode?: 'live' | 'saved';
  /** If true (live mode only), auto-open the Singularity dialog as soon as the component mounts. */
  autoOpenSingularity?: boolean;
};

function roleForNodeType(type?: string) {
  if (!type) return 'system' as const;
  if (type === 'ai-response') return 'agent' as const;
  if (type === 'system-prompt') return 'system' as const;
  if (type === 'start' || type === 'end') return 'system' as const;
  return 'tool' as const;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1">
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70 animate-bounce [animation-delay:-0.2s]" />
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70 animate-bounce [animation-delay:-0.1s]" />
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70 animate-bounce" />
    </div>
  );
}

function SourcesBlock({ sources }: { sources: SourceItem[] }) {
  const [open, setOpen] = useState(false);

  if (!sources.length) return null;

  return (
    <div className="mt-3">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition"
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <LinkIcon className="h-3.5 w-3.5" />
            <span>Sources ({sources.length})</span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-2">
            {sources.map((s, idx) => (
              <div key={idx} className="rounded-md border border-border/70 bg-background/80 p-2 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate text-foreground">
                      {s.title}
                      {s.path ? <span className="text-foreground/65"> • {s.path}</span> : null}
                    </div>
                    {s.snippet ? (
                      <div className="mt-1 whitespace-pre-wrap break-words line-clamp-3 text-xs text-foreground/75">
                        {s.snippet}
                      </div>
                    ) : null}
                  </div>
                  {typeof s.similarity === 'number' ? (
                    <Badge variant="outline" className="shrink-0">
                      {(s.similarity * 100).toFixed(1)}%
                    </Badge>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export default function AgentTheater({
  nodes,
  history,
  onActiveNodeIdChange,
  mode = 'live',
  scoutEnabled,
  showScoutToggle,
  onScoutEnabledChange,
  scoutKeywords,
  onScoutKeywordsChange,
  cortexDisabled,
  cortexMode,
  autoOpenSingularity,
}: Props) {
  const nodeById = useMemo(() => new Map(nodes.map((n) => [String((n as any).id), n])), [nodes]);

  // Keep the Cortex header status steady (avoid flicker while steps stream in).
  const cortexPhrase = 'Conjuring the agents';

  const effectiveCortexMode: 'live' | 'saved' = cortexMode ?? mode;
  const cortexIsLive = effectiveCortexMode === 'live';

  // A stable per-run key (new runs start with a fresh history[0].timestamp).
  const runKey = String(history?.[0]?.timestamp || 'empty');
  const saved = runKey === 'empty' ? undefined : __agentTheaterPlaybackStore.get(runKey);

  const agentLanes = useMemo(() => {
    const set = new Set<string>();
    for (const h of history) {
      if (h.role === 'agent' && typeof h.lane === 'string' && h.lane && h.lane !== 'left') {
        set.add(h.lane);
      }
    }
    return Array.from(set);
  }, [history]);

  const hasAgenticLanes = agentLanes.length > 0;
  const isTwoAgentClassic = agentLanes.length === 2 && agentLanes.every((l) => l === 'agent1' || l === 'agent2');

  const [viewMode, setViewMode] = useState<ViewMode>(hasAgenticLanes ? 'pingpong' : 'lanes');

  const shouldAutoPlay = mode === 'live';

  // Initialize playback from persisted state (live mode) or show completed (saved mode).
  const [playing, setPlaying] = useState(() => {
    if (runKey === 'empty') return false;
    if (!shouldAutoPlay) return false;
    if (!saved) return true;
    // Don't auto-restart finished runs.
    return saved.finished ? false : saved.playing;
  });

  const [cursor, setCursor] = useState(() => {
    if (runKey === 'empty') return 0;
    if (!shouldAutoPlay) return history.length;
    const c = saved?.cursor ?? 0;
    return Math.max(0, Math.min(history.length, c));
  });

  // Local overlays that are only used for the live 3D orchestration visualization
  // (e.g., Auto-Blossom scout forks).
  const [orchOverlayHistory, setOrchOverlayHistory] = useState<any[]>([]);

  // Allow Orchestration3D to append synthetic events without touching the real run history.
  useEffect(() => {
    if (!cortexIsLive) return;
    const key = '__dyad_appendOrchHistory';
    (window as any)[key] = (entries: any[]) => {
      if (!Array.isArray(entries) || !entries.length) return;
      // Keep overlays bounded so bursts can't grow memory without limit.
      setOrchOverlayHistory((prev) => {
        const next = [...prev, ...entries];
        const MAX_OVERLAY = 600;
        return next.length > MAX_OVERLAY ? next.slice(next.length - MAX_OVERLAY) : next;
      });
    };
    return () => {
      if ((window as any)[key]) delete (window as any)[key];
    };
  }, [cortexIsLive]);

  const cursorRef = useRef(cursor);
  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  const mergedHistory = useMemo(() => {
    if (!orchOverlayHistory.length) return history;
    return [...history, ...orchOverlayHistory];
  }, [history, orchOverlayHistory]);

  // Build orchestration, but cap it in live mode so bursts of blossoms/KB nodes don't explode the graph.
  const orchestrationRaw = useMemo(() => buildOrchestrationFromTheaterHistory(mergedHistory), [mergedHistory]);

  const orchestration = useMemo(() => {
    if (!cortexIsLive) return orchestrationRaw;

    const MAX_EVENTS = 2000;
    const MAX_NODES = 500;
    const MAX_BLOSSOMS = 40;

    const events = orchestrationRaw.events.length > MAX_EVENTS ? orchestrationRaw.events.slice(-MAX_EVENTS) : orchestrationRaw.events;

    // Find blossoms present in the (capped) events, most-recent first.
    const blossomLast = new Map<string, number>();
    for (const e of events) {
      // Blossom ids look like "blossom:*" in this graph.
      if (String(e.from).startsWith('blossom:')) blossomLast.set(String(e.from), e.ts);
      if (String(e.to).startsWith('blossom:')) blossomLast.set(String(e.to), e.ts);
    }
    const keepBlossoms = new Set(
      Array.from(blossomLast.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, MAX_BLOSSOMS)
        .map(([id]) => id)
    );

    // Keep nodes referenced by the capped events, plus orchestrator, plus top blossoms.
    const keepNodeIds = new Set<string>(['orchestrator']);
    for (const e of events) {
      keepNodeIds.add(String(e.from));
      keepNodeIds.add(String(e.to));
    }

    // Ensure we keep at least the most recent blossoms even if their events were trimmed.
    for (const b of keepBlossoms) keepNodeIds.add(b);

    let nodes = orchestrationRaw.nodes.filter((n) => {
      const id = String((n as any).id);
      if (!keepNodeIds.has(id)) return false;
      if (String((n as any).type) === 'blossom' && keepBlossoms.size && !keepBlossoms.has(id) && id !== 'blossom:main') return false;
      return true;
    });

    // Hard cap nodes as a final safety valve.
    if (nodes.length > MAX_NODES) {
      const core = nodes.filter((n) => ['orchestrator', 'agent'].includes(String((n as any).type)));
      const rest = nodes.filter((n) => !['orchestrator', 'agent'].includes(String((n as any).type)));
      nodes = [...core, ...rest.slice(0, Math.max(0, MAX_NODES - core.length))];
    }

    return {
      nodes,
      events,
      __capped: true as const,
      __capInfo: {
        maxEvents: MAX_EVENTS,
        maxNodes: MAX_NODES,
        maxBlossoms: MAX_BLOSSOMS,
        rawNodes: orchestrationRaw.nodes.length,
        rawEvents: orchestrationRaw.events.length,
      },
    } as any;
  }, [cortexIsLive, orchestrationRaw]);

  const orchestrationCursor = useMemo(() => {
    const hLen = Math.max(0, mergedHistory.length);
    const oLen = Math.max(0, orchestration.events.length);
    if (!hLen || !oLen) return 0;
    const frac = Math.min(1, Math.max(0, cursor / hLen));
    return Math.round(frac * oLen);
  }, [cursor, mergedHistory.length, orchestration.events.length]);

  const [typingFor, setTypingFor] = useState<null | { lane: string; label: string; role: 'agent' | 'tool' | 'system'; nodeId: string }>(
    null
  );

  const [show3D, setShow3D] = useState(false);
  const [showOrchestration3D, setShowOrchestration3D] = useState(() => {
    if (runKey === 'empty') return false;
    if (cortexDisabled) return false;
    // Restore prior open state for this run (if any).
    const restored = __singularityOpenStore.get(runKey);
    if (typeof restored === 'boolean') return restored;
    // Default behavior for new live runs: keep Singularity open if requested.
    if (cortexIsLive && autoOpenSingularity) return true;
    return false;
  });
  const allowSingularityCloseRef = useRef(false);
  const userClosedSingularityRef = useRef(false);
  const lastRunKeyRef = useRef<string>(runKey);
  const [singularityResetKey, setSingularityResetKey] = useState(0);
  const [ignoredCloseAttempts, setIgnoredCloseAttempts] = useState(0);

  // When the run changes, reset the "user closed" latch and restore prior open state for that run.
  useEffect(() => {
    if (lastRunKeyRef.current === runKey) return;
    lastRunKeyRef.current = runKey;
    userClosedSingularityRef.current = false;

    if (runKey === 'empty') {
      setShowOrchestration3D(false);
      return;
    }

    if (cortexDisabled) {
      setShowOrchestration3D(false);
      return;
    }

    const restored = __singularityOpenStore.get(runKey);
    if (typeof restored === 'boolean') {
      setShowOrchestration3D(restored);
      return;
    }

    if (cortexIsLive && autoOpenSingularity) {
      setShowOrchestration3D(true);
    } else {
      setShowOrchestration3D(false);
    }
  }, [runKey, cortexDisabled, cortexIsLive, autoOpenSingularity]);

  // If live mode requests auto-open, keep it open even if something tries to dismiss it.
  // (Unless the user explicitly closed it.)
  useEffect(() => {
    if (!cortexIsLive) return;
    if (!autoOpenSingularity) return;
    if (cortexDisabled) return;
    if (userClosedSingularityRef.current) return;
    if (runKey === 'empty') return;

    if (!showOrchestration3D) {
      const t = window.setTimeout(() => setShowOrchestration3D(true), 50);
      return () => window.clearTimeout(t);
    }
  }, [cortexIsLive, autoOpenSingularity, cortexDisabled, runKey, showOrchestration3D]);

  // Persist Singularity open state for this run.
  useEffect(() => {
    if (runKey === 'empty') return;
    __singularityOpenStore.set(runKey, showOrchestration3D);
  }, [runKey, showOrchestration3D]);

  const didAutoOpenSingularityRef = useRef(false);
  useEffect(() => {
    if (!cortexIsLive) return;
    if (!autoOpenSingularity) return;
    if (cortexDisabled) return;
    if (didAutoOpenSingularityRef.current) return;
    didAutoOpenSingularityRef.current = true;
    setShowOrchestration3D(true);
  }, [autoOpenSingularity, cortexIsLive, cortexDisabled]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // IMPORTANT: prevLenRef tracks the last *history length* we've seen.
  // On remount, it should start at the last known length so we don't treat the first render as a "new run".
  const prevLenRef = useRef<number>(saved?.lastLen ?? history.length);
  const prevRunKeyRef = useRef(runKey);

  // If this component remounts, restore playback state for the same run.
  useEffect(() => {
    if (prevRunKeyRef.current === runKey) return;
    prevRunKeyRef.current = runKey;

    setTypingFor(null);
    onActiveNodeIdChange?.(null);

    if (runKey === 'empty') {
      setCursor(0);
      setPlaying(false);
      prevLenRef.current = 0;
      return;
    }

    if (!shouldAutoPlay) {
      setCursor(history.length);
      setPlaying(false);
      prevLenRef.current = history.length;
      return;
    }

    const nextSaved = __agentTheaterPlaybackStore.get(runKey);
    if (nextSaved) {
      setCursor(Math.max(0, Math.min(history.length, nextSaved.cursor)));
      setPlaying(nextSaved.finished ? false : nextSaved.playing);
      prevLenRef.current = nextSaved.lastLen;
    } else {
      setCursor(0);
      setPlaying(true);
      prevLenRef.current = history.length;
    }
  }, [runKey, history.length, onActiveNodeIdChange, shouldAutoPlay]);

  // Persist playback state (live mode only).
  useEffect(() => {
    if (runKey === 'empty') return;
    if (!shouldAutoPlay) return;
    const finished = history.length > 0 && cursor >= history.length;
    __agentTheaterPlaybackStore.set(runKey, { cursor, playing, finished, lastLen: history.length });
  }, [runKey, cursor, playing, history.length, shouldAutoPlay]);

  useEffect(() => {
    const prevLen = prevLenRef.current;
    const nextLen = history.length;

    // History cleared: reset playback.
    if (nextLen === 0) {
      setCursor(0);
      setPlaying(false);
      setTypingFor(null);
      onActiveNodeIdChange?.(null);
      prevLenRef.current = 0;
      return;
    }

    // First render for this mounted instance: do NOT reset; state already initialized.
    if (prevLen === 0) {
      prevLenRef.current = nextLen;
      return;
    }

    // History replaced with shorter list (new run / replaced)
    if (nextLen < prevLen) {
      setTypingFor(null);
      setViewMode(hasAgenticLanes ? 'pingpong' : 'lanes');
      onActiveNodeIdChange?.(null);

      if (shouldAutoPlay) {
        setCursor(0);
        setPlaying(true);
      } else {
        setCursor(nextLen);
        setPlaying(false);
      }

      prevLenRef.current = nextLen;
      return;
    }

    // History appended (continued execution): if we were at the end, keep playing so new items animate in.
    if (nextLen > prevLen) {
      setTypingFor(null);
      setCursor((c) => {
        if (c >= prevLen) return prevLen;
        return c;
      });
      if (shouldAutoPlay) {
        setPlaying((p) => p || cursorRef.current >= prevLen);
      }
    }

    prevLenRef.current = nextLen;
  }, [history, hasAgenticLanes, onActiveNodeIdChange, shouldAutoPlay]);

  // REMOVED: cycling Cortex header phrases (caused visible flashing as cursor/history advanced)

  const activeNodeId = useMemo(() => {
    const idx = Math.min(Math.max(cursor - 1, -1), history.length - 1);
    if (idx < 0) return null;
    return history[idx]?.nodeId ?? null;
  }, [cursor, history]);

  useEffect(() => {
    onActiveNodeIdChange?.(typingFor?.nodeId ?? activeNodeId);
  }, [activeNodeId, onActiveNodeIdChange, typingFor?.nodeId]);

  useEffect(() => {
    // Run once: when we reach the end, stop playback and freeze the final state.
    if (history.length > 0 && cursor >= history.length && playing) {
      setPlaying(false);
      setTypingFor(null);
    }
  }, [cursor, history.length, playing]);

  useEffect(() => {
    if (!playing) return;
    if (cursor >= history.length) {
      setTypingFor(null);
      return;
    }

    const next = history[cursor];
    const node = nodeById.get(next.nodeId);
    const type = (node as any)?.type || (node as any)?.data?.type;
    const label = String((node as any)?.data?.label || next.nodeId);

    const role = next.role ?? roleForNodeType(type);
    const lane = (typeof next.lane === 'string' && next.lane ? next.lane : 'left') as string;

    setTypingFor({ lane, label, role, nodeId: next.nodeId });

    const t = window.setTimeout(() => {
      setCursor((c) => Math.min(history.length, c + 1));
      setTypingFor(null);
    }, role === 'agent' ? 650 : 350);

    return () => window.clearTimeout(t);
  }, [playing, cursor, history, nodeById]);

  useEffect(() => {
    // Auto-scroll to bottom when cursor advances
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [cursor, typingFor]);

  const visible = history.slice(0, cursor);

  const agentNameForLane = (lane: string) => {
    if (lane === 'left') return 'System';
    if (lane === 'agent1') return 'Agent 1';
    if (lane === 'agent2') return 'Agent 2';
    // Fallback for multi-agent ids like agent3
    const m = lane.match(/agent(\d+)/i);
    if (m) return `Agent ${m[1]}`;
    return lane;
  };

  const laneColor = (lane: string) => {
    if (lane === 'agent1') return 'border-blue-500/30';
    if (lane === 'agent2') return 'border-fuchsia-500/30';
    const m = lane.match(/agent(\d+)/i);
    if (!m) return '';
    const idx = Number(m[1]);
    const palette = [
      'border-blue-500/30',
      'border-fuchsia-500/30',
      'border-emerald-500/30',
      'border-amber-500/30',
      'border-cyan-500/30',
      'border-rose-500/30',
      'border-lime-500/30',
      'border-violet-500/30',
      'border-sky-500/30',
      'border-orange-500/30',
    ];
    return palette[(idx - 1) % palette.length];
  };

  const alignForLane = (lane: string) => {
    if (lane === 'left') return 'center' as const;
    if (isTwoAgentClassic) return lane === 'agent2' ? ('right' as const) : ('left' as const);
    // Multi-agent: alternate left/right by agent number
    const m = lane.match(/agent(\d+)/i);
    if (m) {
      const n = Number(m[1]);
      return n % 2 === 0 ? ('right' as const) : ('left' as const);
    }
    return 'left' as const;
  };

  const renderBubbleInner = (entry: TheaterHistoryEntry) => {
    const node = nodeById.get(entry.nodeId);
    const type = (node as any)?.type || (node as any)?.data?.type;
    const label = String((node as any)?.data?.label || entry.nodeId);

    const role = entry.role ?? roleForNodeType(type);
    const lane = (typeof entry.lane === 'string' && entry.lane ? entry.lane : 'left') as string;

    const header = role === 'agent' ? agentNameForLane(lane) : role === 'tool' ? 'Tool' : 'System';
    const Icon = role === 'agent' ? Bot : role === 'tool' ? Cpu : Sparkles;

    const bubbleClass =
      role === 'agent'
        ? cn('border bg-card/95 shadow-sm', laneColor(lane))
        : role === 'tool'
          ? 'border border-slate-800 bg-slate-950 text-slate-50 shadow-sm'
          : 'border border-border bg-muted/55 shadow-sm';

    const headerClass = role === 'tool' ? 'text-slate-200' : 'text-foreground/80';
    const metaClass = role === 'tool' ? 'text-slate-400' : 'text-foreground/65';
    const time = new Date(entry.timestamp).toLocaleTimeString();

    const proseClass = cn(
      'prose prose-sm max-w-none break-words',
      'prose-p:my-0 prose-p:leading-7 prose-headings:text-inherit prose-strong:text-inherit prose-code:text-inherit prose-li:text-inherit prose-a:text-primary',
      role === 'tool'
        ? 'prose-invert text-slate-50'
        : 'text-foreground dark:prose-invert'
    );

    return (
      <div className={cn('rounded-lg p-3', bubbleClass)}>
        <div className={cn('mb-1 flex items-center gap-2 text-xs', headerClass)}>
          <Icon className="h-3.5 w-3.5" />
          <span className="font-semibold">{header}</span>
          <span className={cn('truncate', metaClass)}>
            • {label}
          </span>
          <span className={cn('ml-auto', metaClass)}>{time}</span>
        </div>

        <div className={cn('text-sm break-words', role === 'tool' ? 'text-slate-50' : 'text-foreground')}>
          <div className={proseClass}>
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
              }}
              remarkPlugins={[remarkGfm]}
            >
              {entry.message}
            </ReactMarkdown>
          </div>
        </div>

        {role === 'agent' && entry.sources?.length ? <SourcesBlock sources={entry.sources} /> : null}
      </div>
    );
  };

  const renderBubble = (entry: TheaterHistoryEntry, idx: number) => {
    const node = nodeById.get(entry.nodeId);
    const type = (node as any)?.type || (node as any)?.data?.type;
    const role = entry.role ?? roleForNodeType(type);
    const lane = (typeof entry.lane === 'string' && entry.lane ? entry.lane : 'left') as string;

    if (viewMode === 'lanes') {
      // For multi-agent, lanes view collapses to pingpong stream (since we don't have N columns).
      // This keeps UI simple.
      const align = role === 'agent' ? alignForLane(lane) : 'center';
      return (
        <div key={idx} className={cn('flex', align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start')}>
          <div className={cn('max-w-[85%] w-full', align === 'center' && 'max-w-[95%]')}>{renderBubbleInner(entry)}</div>
        </div>
      );
    }

    // pingpong stream
    if (role !== 'agent') {
      return (
        <div key={idx} className="flex justify-center">
          <div className="max-w-[95%] w-full">{renderBubbleInner(entry)}</div>
        </div>
      );
    }

    const align = alignForLane(lane);
    return (
      <div key={idx} className={cn('flex', align === 'right' ? 'justify-end' : 'justify-start')}>
        <div className="max-w-[85%] w-full">{renderBubbleInner(entry)}</div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="shrink-0 border-b bg-card px-3 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">Agent Theater</Badge>
          {hasAgenticLanes ? (
            <div className="flex items-center gap-1.5">
              <Button
                variant={viewMode === 'pingpong' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('pingpong')}
                className="gap-2"
              >
                <MessagesSquare className="h-4 w-4" /> Ping-pong
              </Button>
              <Button
                variant={viewMode === 'lanes' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('lanes')}
                className="gap-2"
              >
                <Columns2 className="h-4 w-4" /> Lanes
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowOrchestration3D(true)}
            className="gap-2"
            disabled={Boolean(cortexDisabled)}
          >
            <Box className="h-4 w-4" /> Cortex
          </Button>
        </div>
      </div>

      {/* Timeline */}
      <div className="rounded-md border bg-background p-3 shrink-0">
        <div className="flex items-center gap-2">
          <div className="h-1 flex-1 rounded-full bg-muted overflow-hidden">
            <div
              className="h-1 bg-primary transition-all"
              style={{ width: `${history.length ? (Math.min(cursor, history.length) / history.length) * 100 : 0}%` }}
            />
          </div>
          <div className="text-xs text-muted-foreground w-16 text-right">
            {history.length ? Math.round((Math.min(cursor, history.length) / history.length) * 100) : 0}%
          </div>
        </div>
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          {history.map((_, idx) => {
            const done = idx < cursor;
            const active = idx === cursor;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setPlaying(false);
                  setTypingFor(null);
                  setCursor(Math.max(0, Math.min(history.length, idx)));
                }}
                className={cn(
                  'h-2.5 w-2.5 rounded-full border transition-all',
                  done ? 'bg-primary border-primary' : 'bg-background border-muted-foreground/30',
                  active && 'ring-2 ring-primary/40 animate-pulse'
                )}
                aria-label={`Jump to step ${idx + 1}`}
              />
            );
          })}
        </div>
      </div>

      <div className="rounded-md border overflow-hidden flex-1 flex flex-col min-h-0">
        {viewMode === 'lanes' && isTwoAgentClassic && (
          <div className="grid grid-cols-3 gap-0 border-b bg-muted/30 shrink-0">
            <div className="p-2 text-xs font-medium text-muted-foreground">System / Tools</div>
            <div className="p-2 text-xs font-medium text-muted-foreground text-center">Agent 1</div>
            <div className="p-2 text-xs font-medium text-muted-foreground text-right">Agent 2</div>
          </div>
        )}

        <ScrollArea className="flex-1" ref={scrollerRef as any}>
          <div className="p-3 space-y-3">
            {visible.map(renderBubble)}

            {typingFor && (
              <div
                className={cn(
                  'flex',
                  viewMode === 'pingpong'
                    ? typingFor.role === 'agent'
                      ? alignForLane(typingFor.lane) === 'right'
                        ? 'justify-end'
                        : 'justify-start'
                      : 'justify-center'
                    : 'justify-start'
                )}
              >
                <div className={cn('max-w-[85%] w-full', typingFor.role !== 'agent' && 'max-w-[95%]')}>
                  <div className={cn('rounded-lg p-3 border shadow-sm', typingFor.role === 'agent' ? 'bg-card/95 border-border' : 'bg-muted/55 border-border')}>
                    <div className="flex items-center gap-2 text-xs mb-1 text-foreground/80">
                      {typingFor.role === 'agent' ? <Bot className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
                      <span className="font-semibold">
                        {typingFor.role === 'agent' ? agentNameForLane(typingFor.lane) : typingFor.role === 'tool' ? 'Tool' : 'System'}
                      </span>
                      <span className="truncate text-foreground/65">• {typingFor.label}</span>
                    </div>
                    <div className="text-sm text-foreground/75">
                      <TypingDots />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      <Dialog open={show3D} onOpenChange={setShow3D}>
        <DialogContent className="w-[96vw] max-w-5xl h-[80vh] overflow-hidden border-border bg-background p-0 text-foreground shadow-2xl">
          <DialogHeader className="border-b border-border bg-background/95 p-4 backdrop-blur-sm">
            <DialogTitle>3D Agent Ping-Pong</DialogTitle>
            <DialogDescription className="sr-only">3D visualization of agent turns.</DialogDescription>
          </DialogHeader>
          <div className="h-[calc(80vh-56px)]">
            <AgentPingPong3D history={history} cursor={cursor} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showOrchestration3D}
        onOpenChange={(next) => {
          // In live mode, keep Singularity open unless the user explicitly closes it.
          if (cortexIsLive && next === false && !allowSingularityCloseRef.current) {
            setIgnoredCloseAttempts((n) => n + 1);
            return;
          }
          setIgnoredCloseAttempts(0);
          setShowOrchestration3D(next);
        }}
      >
        <DialogContent
          className={cn(
            'mx-auto flex h-[90vh] w-[95vw] max-w-[1600px] flex-col overflow-hidden border-border bg-background p-0 text-foreground shadow-2xl',
            // Hide the default Radix close button in live mode; provide an explicit close button instead.
            cortexIsLive && '[&>button.absolute]:hidden'
          )}
          // In live mode, avoid accidental closes (ESC / click-outside / focus-outside) while the graph is busy.
          onEscapeKeyDown={(e) => {
            if (cortexIsLive) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (cortexIsLive) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (cortexIsLive) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            if (cortexIsLive) e.preventDefault();
          }}
        >
          <DialogHeader className="shrink-0 border-b border-border bg-background/95 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <DialogTitle>Cortex</DialogTitle>
                  {showScoutToggle ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>Scout</span>
                      <Switch
                        checked={Boolean(scoutEnabled)}
                        onCheckedChange={(v) => onScoutEnabledChange?.(Boolean(v))}
                      />
                      {scoutEnabled ? (
                        <div className="flex items-center gap-1 ml-1 flex-wrap">
                          {(scoutKeywords || []).map((kw) => (
                            <Badge
                              key={kw}
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0 h-5 gap-1 cursor-pointer hover:bg-destructive/20"
                              onClick={() =>
                                onScoutKeywordsChange?.(
                                  (scoutKeywords || []).filter((k) => k !== kw)
                                )
                              }
                            >
                              {kw}
                              <span className="opacity-60">&times;</span>
                            </Badge>
                          ))}
                          <Input
                            placeholder="+ keyword"
                            className="h-5 w-20 text-[10px] px-1.5 py-0 bg-muted/40 border-muted-foreground/20 focus-visible:w-28 transition-all"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const val = (e.target as HTMLInputElement).value.trim().toLowerCase();
                                if (val && !(scoutKeywords || []).includes(val)) {
                                  onScoutKeywordsChange?.([...(scoutKeywords || []), val]);
                                }
                                (e.target as HTMLInputElement).value = '';
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <DialogDescription className="sr-only">3D orchestration view of the current run.</DialogDescription>
                {(orchestration as any).__capped ? (
                  <div className="text-xs text-muted-foreground mt-1">
                    Showing a simplified view during bursts (last {(orchestration as any).__capInfo?.maxEvents} events, up to {(orchestration as any).__capInfo?.maxNodes} nodes).
                  </div>
                ) : null}
                {cortexIsLive && ignoredCloseAttempts > 0 ? (
                  <div className="text-xs text-muted-foreground mt-1">
                    Ignored close attempts: {ignoredCloseAttempts}
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-3 shrink-0">
                {cortexIsLive ? (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="font-medium text-foreground/90">{cortexPhrase}</span>
                    <ThinkingDots className="text-muted-foreground" />
                  </div>
                ) : null}

                {cortexIsLive ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      userClosedSingularityRef.current = true;
                      allowSingularityCloseRef.current = true;
                      setShowOrchestration3D(false);
                      // Reset the guard on the next tick.
                      window.setTimeout(() => {
                        allowSingularityCloseRef.current = false;
                      }, 0);
                    }}
                  >
                    Close
                  </Button>
                ) : null}
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0">
            <SingularityErrorBoundary
              onReset={() => {
                // Force a remount of Orchestration3D if it crashed.
                setSingularityResetKey((k) => k + 1);
              }}
            >
              <Orchestration3D
                key={singularityResetKey}
                live={cortexIsLive}
                autoBlossom={Boolean(scoutEnabled)}
                scoutKeywords={scoutKeywords}
                {...(orchestration as any)}
                eventCursor={orchestrationCursor}
                semanticConfig={{
                  enabled: localStorage.getItem('orch3d.semanticEnabled') === '1',
                  temp: Number(localStorage.getItem('orch3d.semanticTemp') ?? 0.02),
                  G: Number(localStorage.getItem('orch3d.semanticG') ?? 25),
                  K: Number(localStorage.getItem('orch3d.semanticK') ?? 200),
                  topK: Number(localStorage.getItem('orch3d.topK') ?? 6),
                  affinityThreshold: Number(localStorage.getItem('orch3d.affinityThreshold') ?? 0.05),
                  anchorEnabled: localStorage.getItem('orch3d.anchorEnabled') !== '0',
                  promptText: String(localStorage.getItem('orch3d.promptText') ?? ''),
                }}
              />

            </SingularityErrorBoundary>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}